import React, { PureComponent } from 'react';
import { Translate } from 'react-localize-redux';
import ReactTooltip from 'react-tooltip';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { REMAINING_TICKETS_LIMIT } from '../../constants';
import renderCapacityIndicator from '../day/render-capacity-indicator';

/**
 * Container for timeslot
 */
const Container = styled.div`
  ${({ disabled, isWeekday, theme }) =>
    disabled
      ? css`
          background: #454545;
          cursor: not-allowed;
          opacity: 0.3;
        `
      : css`
          background: ${isWeekday ? theme.weekday : theme.holiday};
          cursor: pointer;
        `}

  display: flex;
  align-items: center;
  justify-content: space-around;
  padding: 0.5em;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16), 0 3px 6px rgba(0, 0, 0, 0.23);
  @media (max-width: 767px) {
    padding: 1em 0.5em;
  }
`;

/**
 * Container to ensure that days without capacity data render similarly
 */
const CapacityContainer = styled.div`
  display: flex;
  width: 15%;
  position: relative;
`;

/**
 * Time heading
 */
const Time = styled.div`
  font-size: 1.3em;
`;

const TicketIndicator = styled.div`
  position: absolute;
  top: 30%;
  left: 33%;
  font-size: 0.8em;
`;

/**
 * Time Slot Presentational Component
 */
class Timeslot extends PureComponent {
  state = {
    percent: null
  };
  componentDidMount() {
    this.calculatePercentage();
  }
  componentDidUpdate(prevProps) {
    if (
      prevProps.playerCapacity !== this.props.playerCapacity ||
      prevProps.playerCount !== this.props.playerCount
    ) {
      this.calculatePercentage();
    }
  }
  calculatePercentage() {
    // const percent = this.props.playerCapacity
    //   ? (100 * this.props.playerCount) / this.props.playerCapacity
    //   : 100;
    const percent = (() => {
      if (this.props.playerCapacity) {
        const p = (100 * this.props.playerCount) / this.props.playerCapacity;
        // fallthrough if overbooking is occurring
        if (p < 100) return p;
      }
      return 100;
    })();

    this.setState({ percent });
  }
  /**
   * On click callback
   */
  onClick = () => {
    if (this.state.percent === 100 || this.props.cancelled) {
      return;
    }
    this.props.onClick(this.props.value, this.props.percent);
  };

  renderIndicator = () => {
    return renderCapacityIndicator(this.state.percent, !this.props.cancelled);
  };

  renderRemainingTickets = () => {
    if (!this.props.playerCapacity) {
      return;
    }
    const diff = this.props.playerCapacity - this.props.playerCount;
    if (!diff || diff > REMAINING_TICKETS_LIMIT) {
      return;
    }
    if (diff < 0) {
      // overbooking
      return;
    }
    return <TicketIndicator>{diff}</TicketIndicator>;
  };

  /**
   * @inheritdoc
   */
  render() {
    const disabled = this.props.cancelled || this.state.percent === 100;
    const isWeekday = !(
      this.props.date.day() === 0 ||
      this.props.date.day() === 6 ||
      this.props.isHoliday
    );
    return (
      <Translate>
        {({ translate }) => (
          <Container
            data-tip={
              this.state.percent === 100 ? translate('timeslot.fullText') : ''
            }
            disabled={disabled}
            isWeekday={isWeekday}
            onClick={this.onClick}
          >
            <Time>{this.props.date.format('HH:mm')}</Time>
            <CapacityContainer>
              {this.renderIndicator()}
              {this.renderRemainingTickets()}
            </CapacityContainer>
            <ReactTooltip />
          </Container>
        )}
      </Translate>
    );
  }
}

Timeslot.propTypes = {
  date: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired,
  playerCount: PropTypes.number.isRequired,
  playerCapacity: PropTypes.number.isRequired,
  isHoliday: PropTypes.bool.isRequired,
  cancelled: PropTypes.bool,
  value: PropTypes.string.isRequired
};

export default Timeslot;
